import { timestampCodec } from '../../validation/codecs';
import { mixed, required, t } from '../../validation/validator';
import { CallResponseMixinSchema } from "../common/callResponseSchema";

// Request schemas
export const GetTeamReportSchema = t.intersection([
    t.type({
        team_id: t.string
    }),
    t.partial({
        from_date: t.union([t.number, t.null, t.undefined]),
        to_date: t.union([t.number, t.null, t.undefined])
    })
]);

export const GetTeamReportResponseSchema = t.type({
    overview: t.array(t.any),
    users: t.array(t.any),
});

export const GetUserReportSchema = t.intersection([
    t.type({
        user_ids: t.array(t.string)
    }),
    t.partial({
        from_date: t.union([timestampCodec, t.string, t.null]),
        to_date: t.union([timestampCodec, t.string, t.null])
    })
]);

export const GetUserReportResponseSchema = t.array(t.any);

export type GetTeamReportResponse = t.TypeOf<typeof GetTeamReportResponseSchema>;
export type GetTeamReportRequest = t.TypeOf<typeof GetTeamReportSchema>;
export type GetUserReportRequest = t.TypeOf<typeof GetUserReportSchema>;
export type GetUserReportResponse = t.TypeOf<typeof GetUserReportResponseSchema>;

export const AccountReportRequestSchema = t.intersection([
    t.type({
        account_id: t.number,
    }),
    t.partial({
        from_date: t.union([timestampCodec, t.null]),
        to_date: t.union([timestampCodec, t.null])
    })
]);

export const AccountReportResultSchema = t.type({
    user_doc_id: t.string,
    user_created_at: t.union([timestampCodec, t.null]),
    user_id: t.number,
    last_active_at: t.union([timestampCodec, t.null]),
    gameplay_duration: t.number,
    total_score: t.union([t.number, t.null]),
    scan_rate: t.union([t.number, t.null]),
    scan_timing: t.union([t.number, t.null]),
    critical_scan_score: t.union([t.number, t.null]),
    decision_score: t.union([t.number, t.null]),
    awareness_score: t.union([t.number, t.null]),
    packing_rate: t.union([t.number, t.null]),
    user_first_name: t.string,
    user_last_name: t.string,
    previous_gameplay_duration: t.union([t.number, t.null]),
    previous_total_score: t.union([t.number, t.null]),
    previous_scan_rate: t.union([t.number, t.null]),
    previous_scan_timing: t.union([t.number, t.null]),
    previous_critical_scan_score: t.union([t.number, t.null]),
    previous_decision_score: t.union([t.number, t.null]),
    previous_awareness_score: t.union([t.number, t.null]),
    previous_packing_rate: t.union([t.number, t.null])
});

export const AccountReportResponseSchema = t.type({
    overview: t.array(AccountReportResultSchema),
    users: t.any // This matches the getUserReport response type
});

export const GetMemberReportSchema = t.intersection([
    t.type({
        account_id: t.number,
        member_ids: t.array(t.string)
    }),
    t.partial({
        from_date: t.union([timestampCodec, t.string, t.null]),
        to_date: t.union([timestampCodec, t.string, t.null])
    })
]);

export const GetMemberReportResponseSchema = t.array(t.type({
    user_id: t.number,
    play_session_id: t.number,
    play_session_type: t.string,
    game_mode: t.string,
    rating: t.number,
    game_speed: t.number,
    user_doc_id: t.string,
    started_at: t.union([timestampCodec, t.null]),
    ended_at: t.union([timestampCodec, t.null]),
    gameplay_duration: t.number,
    total_score: t.number,
    total_score_target: t.number,
    scan_rate: t.number,
    scan_rate_target: t.number,
    scan_timing: t.number,
    scan_timing_target: t.number,
    critical_scan_score: t.number,
    critical_scan_target: t.number,
    decision_score: t.number,
    decision_score_target: t.number,
    awareness_score: t.number,
    awareness_score_target: t.number,
    scenario_doc_id: t.string,
    play_positions: t.array(t.string),
    scan_symmetry: t.number,
    user_name: t.type({
        first: t.string,
        last: t.string
    })
}));

export type GetMemberReportResponse = t.TypeOf<typeof GetMemberReportResponseSchema>;
export type GetMemberReportRequest = t.TypeOf<typeof GetMemberReportSchema>;

export const ReportApis = {
    Reports: {
        GetAccountReport: {
            Name: 'reports-getAccountReport',
            Request: AccountReportRequestSchema,
            Response: mixed([
                required({ 
                    data: AccountReportResponseSchema
                }), 
                CallResponseMixinSchema
            ]),
        },
        GetMemberReport: {
            Name: 'reports-getMemberReport',
            Request: GetMemberReportSchema,
            Response: GetMemberReportResponseSchema
        },
        GetTeamReport: {
            Name: 'reports-getTeamReport',
            Request: GetTeamReportSchema,
            Response: GetTeamReportResponseSchema
        },
        GetUserReport: {
            Name: 'reports-getUserReport',
            Request: GetUserReportSchema,
            Response: GetUserReportResponseSchema
        }
    }
};